export const ROLES = {
  admin: { label: 'Admin', color: 'var(--brand-red)' },
  rd: { label: 'R&D', color: '#7c3aed' },
  regulatory: { label: 'Regulatory', color: '#0891b2' },
  qa: { label: 'QA', color: '#d97706' },
  coo: { label: 'ED Operations', color: '#059669' },
  marketing: { label: 'Marketing', color: '#db2777' },
  cco: { label: 'CCO', color: '#111827' },
};

export const STAGES = {
  1: { label: 'Packaging Design Approval Check List (Before NAFDAC Approval)', short: 'NAFDAC', color: 'var(--stage1)' },
  2: { label: 'Packaging Design Approval Check List (Before Release of Artwork to Supplier)', short: 'Pre-Supplier', color: 'var(--stage2)' },
  3: { label: 'POSITION PROOF Approval Check List', short: 'Pos. Proof', color: 'var(--stage3)' },
  4: { label: 'COLOUR PROOF / CHROMALIN Approval Check List', short: 'Col. Proof', color: 'var(--stage4)' },
};

export const STATUS_LABELS = {
  pending: { label: 'Pending', badge: 'badge-gray' },
  approved: { label: 'Approved', badge: 'badge-success' },
  changes_requested: { label: 'Changes Requested', badge: 'badge-danger' },
  in_progress: { label: 'In Progress', badge: 'badge-info' },
  not_started: { label: 'Not Started', badge: 'badge-gray' },
  completed: { label: 'Completed', badge: 'badge-success' },
  active: { label: 'Active', badge: 'badge-info' },
  on_hold: { label: 'On Hold', badge: 'badge-warning' },
};

export const CHECKLIST_ITEMS = [
  'Barcode',
  'NAFDAC Number',
  'MANCAP Number',
  'Masterbrand Logo',
  'FSC Logo',
  'Dispose Properly Symbol',
  'Customer Care Hotline',
  'Ingredient List',
  'Nutritional Information',
  'Statutory Use Advice',
  'Guarantee Statement',
  'Pack Size / Grammage',
  'Address for Manufacturer/Co-packer',
  'Artwork Layout',
  'Other Logos',
];

export const OPTIONAL_REVIEW_ROLES = ['coo', 'marketing'];

export const getRoleBadgeClass = (role) => {
  const map = {
    admin: 'badge-danger',
    rd: 'badge-stage1',
    regulatory: 'badge-stage2',
    qa: 'badge-warning',
    coo: 'badge-success',
    marketing: 'badge-info',
    cco: 'badge-gray',
    supplier: 'badge-gray',
  };
  return map[role] || 'badge-gray';
};

export const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-NG', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

export const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('en-NG', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export const getArtworkPreviewKind = (stageData = {}) => {
  let artworkUrl = stageData.artworkUrl || '';
  let artworkFileName = stageData.artworkFileName || '';
  let artworkMimeType = stageData.artworkMimeType || '';
  let artworkData = stageData.artworkData;

  // Stage 3 / Stage 4 no longer keep a root-level file — fall back to the
  // first available per-supplier submission so list/preview UIs still work.
  if (!artworkUrl && !artworkData && !artworkFileName && Array.isArray(stageData.submissions) && stageData.submissions.length > 0) {
    const firstWithFile = stageData.submissions.find(s => s.artworkUrl || s.artworkData || s.artworkFileName);
    if (firstWithFile) {
      artworkUrl = firstWithFile.artworkUrl || '';
      artworkFileName = firstWithFile.artworkFileName || '';
      artworkMimeType = firstWithFile.artworkMimeType || '';
      artworkData = firstWithFile.artworkData;
    }
  }

  if (artworkMimeType?.toLowerCase().includes('pdf') || artworkFileName.toLowerCase().endsWith('.pdf') || artworkUrl.toLowerCase().endsWith('.pdf')) {
    return 'pdf';
  }

  if (artworkUrl || artworkData) {
    return 'image';
  }

  return 'placeholder';
};

export const hasPendingReviewForRole = (stage, role, artwork = null, stageNum = null, userId = null, userEmail = '', userName = '') => {
  if (!stage) return false;
  const hasSupplierSubmissions = Array.isArray(stage.submissions) && stage.submissions.length > 0;
  if (stage.status === 'completed' && !hasSupplierSubmissions) return false;

  const normalizedUserId = userId ? String(userId?._id || userId) : '';
  const normalizedUserEmail = (userEmail || '').toLowerCase().trim();
  const normalizedUserName = (userName || '').toLowerCase().trim();
  const belongsToReviewer = (approval) => {
    if (approval.role !== role || approval.status !== 'pending') return false;
    const assignedUserId = approval.assignedUserId ? String(approval.assignedUserId?._id || approval.assignedUserId) : '';
    const assignedUserEmail = (approval.assignedUserEmail || '').toLowerCase().trim();
    const assignedUserName = (approval.assignedUserName || '').toLowerCase().trim();
    if (!assignedUserId && !assignedUserEmail && !assignedUserName) return true;
    return (normalizedUserId && assignedUserId === normalizedUserId) ||
      (normalizedUserEmail && assignedUserEmail === normalizedUserEmail) ||
      (normalizedUserName && assignedUserName === normalizedUserName);
  };

  if (Array.isArray(stage.approvals) && stage.approvals.length > 0) {
    const hasPendingApproval = stage.approvals.some(belongsToReviewer);
    return hasPendingApproval && (!artwork || stageNum === null || canUserApprove(artwork, stageNum, role, userId, userEmail, null, userName));
  }

  if (Array.isArray(stage.submissions) && stage.submissions.length > 0) {
    const hasPendingApproval = stage.submissions.some((submission) =>
      Array.isArray(submission.approvals) && submission.approvals.some(belongsToReviewer)
    );
    return hasPendingApproval && (!artwork || stageNum === null || canUserApprove(artwork, stageNum, role, userId, userEmail, null, userName));
  }

  return false;
};

export const getCloudinaryThumbnailUrl = (artworkUrl, width = 108, height = 108) => {
  if (!artworkUrl || !artworkUrl.includes('res.cloudinary.com/')) return artworkUrl;

  const uploadMarker = '/image/upload/';
  const uploadIndex = artworkUrl.indexOf(uploadMarker);
  if (uploadIndex === -1) return artworkUrl;

  const transformation = `f_auto,q_auto,w_${width},h_${height},c_fill`;
  const transformationStart = uploadIndex + uploadMarker.length;
  return `${artworkUrl.slice(0, transformationStart)}${transformation}/${artworkUrl.slice(transformationStart)}`;
};

export const getArtworkSupplierDisplayName = (artwork = {}) => {
  const isSupplierStage = artwork.currentStage === 3 || artwork.currentStage === 4;
  const assignedSuppliers = artwork.supplierAssignmentInfo?.assignedSuppliers || artwork.suppliers || [];

  if (!isSupplierStage) return '—';

  if (!Array.isArray(assignedSuppliers) || assignedSuppliers.length === 0) {
    return '—';
  }

  const supplierNames = assignedSuppliers
    .filter((supplier) => supplier?.name)
    .map((supplier) => supplier.name);

  return supplierNames.length > 0 ? supplierNames.join(', ') : '—';
};

// Generalized per-supplier submission status for Stage 3 or Stage 4.
export const getStageSupplierSubmissionStatus = (artwork = {}, stageNum = 3) => {
  const assignedSuppliers = artwork.supplierAssignmentInfo?.assignedSuppliers || artwork.suppliers || [];
  if (!Array.isArray(assignedSuppliers) || assignedSuppliers.length === 0) {
    return [];
  }

  const stageData = artwork[`stage${stageNum}`] || {};
  const submissions = Array.isArray(stageData.submissions) ? stageData.submissions : [];

  return assignedSuppliers.map((supplier) => {
    const supplierId = String(supplier._id || supplier);
    const supplierName = supplier?.name || supplier?.email || String(supplierId);
    const supplierNameLower = String(supplierName).toLowerCase();
    // Consider a submission as 'submitted' only if a submission record exists AND it contains a file
    const submissionRecord = submissions.find((submission) => {
      const sid = submission.supplierId ? String(submission.supplierId._id || submission.supplierId) : null;
      return sid === supplierId || String((submission.supplierId && (submission.supplierId.name || submission.supplierId.email)) || '').toLowerCase() === supplierNameLower;
    });

    const submissionHasFile = submissionRecord ? Boolean(submissionRecord.artworkUrl || submissionRecord.artworkFileName || submissionRecord.artworkData) : false;
    const submitted = submissionHasFile;
    return {
      supplierId,
      supplierName,
      submitted,
    };
  });
};

export const getSupplierStageSubmission = (stageData = {}, supplierId = '') => {
  const submissions = Array.isArray(stageData.submissions) ? stageData.submissions : [];
  if (!supplierId) return submissions[0] || null;
  const normalizedSupplierId = normalizeId(supplierId);
  return submissions.find((submission) => {
    const sid = normalizeId((submission.supplierId && submission.supplierId._id) || submission.supplierId);
    return sid === normalizedSupplierId;
  }) || null;
};

export const getSupplierStageSubmissionFromArtwork = (artwork = {}, stageNum = 3, supplierId = '') => {
  const stageData = artwork[`stage${stageNum}`] || {};
  return getSupplierStageSubmission(stageData, supplierId);
};

export const isSupplierArtworkCompleted = (artwork = {}, supplierId = '') => {
  const stage3Submission = getSupplierStageSubmissionFromArtwork(artwork, 3, supplierId);
  const stage4Submission = getSupplierStageSubmissionFromArtwork(artwork, 4, supplierId);
  return stage3Submission?.status === 'completed' &&
    stage4Submission?.status === 'completed' &&
    Boolean(stage4Submission.adminApproval?.uploadedAt);
};

export const isSupplierStageSubmissionPendingReview = (artwork = {}, stageNum = 3, supplierId = '') => {
  const stageData = artwork[`stage${stageNum}`];
  if (!stageData || stageData.status !== 'in_progress') return false;
  const submission = getSupplierStageSubmissionFromArtwork(artwork, stageNum, supplierId);
  if (!submission) return false;
  return submission.status === 'pending';
};

export const isSupplierStageSubmissionChangesRequested = (artwork = {}, stageNum = 3, supplierId = '') => {
  const submission = getSupplierStageSubmissionFromArtwork(artwork, stageNum, supplierId);
  if (!submission) return false;
  return Array.isArray(submission.approvals) && submission.approvals.some((approval) => approval.status === 'changes_requested');
};

export const doesSupplierStageSubmissionRequireUpload = (artwork = {}, stageNum = 3, supplierId = '') => {
  const submission = getSupplierStageSubmissionFromArtwork(artwork, stageNum, supplierId);
  return !(submission && (submission.artworkUrl || submission.artworkData || submission.artworkFileName));
};

export const doesSupplierStageSubmissionRequireAction = (artwork = {}, stageNum = 3, supplierId = '') => {
  const stageData = artwork[`stage${stageNum}`];
  if (!stageData) return false;
  return doesSupplierStageSubmissionRequireUpload(artwork, stageNum, supplierId) || isSupplierStageSubmissionChangesRequested(artwork, stageNum, supplierId);
};

// Kept for backward compatibility — Stage 3 specifically.
export const getStage3SupplierSubmissionStatus = (artwork = {}) => getStageSupplierSubmissionStatus(artwork, 3);

export const doesArtworkRequireAdminFinalize = (artwork = {}) => {
  if (artwork.overallStatus === 'completed') return false;

  const submissions = Array.isArray(artwork.stage4?.submissions) ? artwork.stage4.submissions : [];
  return submissions.some((submission) => {
    if (!submission) return false;
    if (submission.adminApproval && submission.adminApproval.uploadedAt) return false;
    return true;
  });
};

export const getApprovalCardDisplayState = (approval = {}, stageData = {}, currentUserRole = '') => {
  if (!approval || approval.status !== 'pending') {
    return { label: 'Pending', badge: 'badge-gray' };
  }

  const approvals = stageData?.approvals || [];
  const role = approval.role;
  const roleApproved = (targetRole) => {
    const roleApprovals = approvals.filter((item) => item.role === targetRole);
    return roleApprovals.length > 0 && roleApprovals.every((item) => item.status === 'approved');
  };

  const roleReadyForNextStep = (targetRole) => {
    const TEAM_ROLES = ['rd', 'regulatory', 'qa'];
    if (!TEAM_ROLES.includes(targetRole)) {
      return roleApproved(targetRole);
    }

    const hasHead = approvals.some((item) => item.role === targetRole && item.teamLevel === 'head');
    if (!hasHead) {
      return roleApproved(targetRole);
    }

    return approvals.some((item) => item.role === targetRole && item.teamLevel === 'head' && item.status === 'approved');
  };

  const isCurrentUserRole = currentUserRole && role === currentUserRole;

  if (role === 'regulatory') {
    return roleReadyForNextStep('rd')
      ? { label: isCurrentUserRole ? 'Awaiting Your Review' : 'Awaiting Review', badge: 'badge-warning' }
      : { label: 'Waiting for earlier approvals', badge: 'badge-gray' };
  }

  if (['qa', 'coo', 'marketing'].includes(role)) {
    const canAdvance = roleReadyForNextStep('rd') && roleReadyForNextStep('regulatory');
    return canAdvance
      ? { label: isCurrentUserRole ? 'Awaiting Your Review' : 'Awaiting Review', badge: 'badge-warning' }
      : { label: 'Waiting for earlier approvals', badge: 'badge-gray' };
  }

  if (role === 'cco') {
    return roleReadyForNextStep('qa')
      ? { label: isCurrentUserRole ? 'Awaiting Your Review' : 'Awaiting Review', badge: 'badge-warning' }
      : { label: 'Waiting for earlier approvals', badge: 'badge-gray' };
  }

  if (role === 'rd') {
    return { label: isCurrentUserRole ? 'Awaiting Your Review' : 'Awaiting Review', badge: 'badge-warning' };
  }

  return { label: 'Pending', badge: 'badge-gray' };
};

export const shouldShowReminderAction = (approval = {}, stageData = {}, currentUserRole = '') => {
  if (!approval || approval.status !== 'pending') return false;
  if (currentUserRole !== 'admin') return false;

  const displayState = getApprovalCardDisplayState(approval, stageData, currentUserRole);
  return displayState.label === 'Awaiting Your Review' || displayState.label === 'Awaiting Review';
};

export const getPendingReviewersForStage = (stageData = {}, artwork = {}) => {
  const approvals = stageData?.approvals || [];
  const currentStage = artwork?.currentStage;
  // If we're at Stage 2 but admin hasn't uploaded the Stage 2 artwork yet,
  // make it explicit that the item is waiting on admin upload rather than
  // showing assigned reviewers (R&D) prematurely.
  if (currentStage === 2 && stageData?.status !== 'in_progress') {
    return ['Admin (upload required)'];
  }
  if (currentStage === 3 || currentStage === 4 || Array.isArray(stageData?.submissions)) {
    const submissions = Array.isArray(stageData?.submissions) ? stageData.submissions : [];
    if (submissions.length === 0) {
      const assigned = artwork?.supplierAssignmentInfo?.assignedSuppliers || artwork?.suppliers || [];
      const names = (Array.isArray(assigned) ? assigned : [])
        .map((s) => {
          if (!s) return null;
          if (typeof s === 'string' || typeof s === 'number') return null;
          return s.name || s.email || null;
        })
        .filter(Boolean);
      const uniqueNames = [...new Set(names)];
      return uniqueNames.length > 0 ? uniqueNames : [];
    }

    const getReviewerName = (approval) =>
      approval?.assignedUserName || approval?.userName || ROLES[approval?.role]?.label || approval?.role;

    const getSubmissionPendingNames = (submission) => {
      const submissionApprovals = Array.isArray(submission?.approvals) ? submission.approvals : [];
      if (submissionApprovals.length === 0) return [];

      const roleStatus = (role) => {
        const roleApprovals = submissionApprovals.filter((a) => a.role === role);
        if (roleApprovals.length === 0) return null;
        if (roleApprovals.some((a) => a.status === 'pending')) return 'pending';
        return 'approved';
      };

      const isAnyPending = (roles) => roles.some((role) => roleStatus(role) === 'pending');

      // Determine the first unfinished reviewer role group for this submission.
      if (roleStatus('rd') === 'pending') {
        return submissionApprovals
          .filter((a) => a.role === 'rd' && a.status === 'pending')
          .map(getReviewerName);
      }
      if (roleStatus('regulatory') === 'pending') {
        return submissionApprovals
          .filter((a) => a.role === 'regulatory' && a.status === 'pending')
          .map(getReviewerName);
      }
      if (isAnyPending(['qa', 'coo', 'marketing'])) {
        return submissionApprovals
          .filter((a) => ['qa', 'coo', 'marketing'].includes(a.role) && a.status === 'pending')
          .map(getReviewerName);
      }
      if (roleStatus('cco') === 'pending') {
        return submissionApprovals
          .filter((a) => a.role === 'cco' && a.status === 'pending')
          .map(getReviewerName);
      }

      if (submission.status === 'pending') {
        return ['Admin'];
      }

      return [];
    };

    const pendingNames = submissions.flatMap(getSubmissionPendingNames).filter(Boolean);
    const uniqueNames = [...new Set(pendingNames)];
    return uniqueNames.length > 0 ? uniqueNames : [];
  }

  if (!approvals.length || stageData?.status !== 'in_progress') return [];

  const getAssignedReviewerName = (approval) =>
    approval?.assignedUserName || approval?.userName || ROLES[approval?.role]?.label || approval?.role;

  const uniqueReviewerNames = (approvalList) =>
    [...new Set(approvalList.map(getAssignedReviewerName).filter(Boolean))];

  const pending = (role) => approvals.filter((approval) => approval.role === role && approval.status === 'pending');
  const roleApproved = (role) => {
    const roleApprovals = approvals.filter((approval) => approval.role === role);
    return roleApprovals.length > 0 && roleApprovals.every((approval) => approval.status === 'approved');
  };

  if (!roleApproved('rd')) return uniqueReviewerNames(pending('rd'));
  if (!roleApproved('regulatory')) return uniqueReviewerNames(pending('regulatory'));
  if (!roleApproved('qa')) {
    return uniqueReviewerNames([
      ...pending('qa'),
      ...OPTIONAL_REVIEW_ROLES.flatMap((role) => pending(role))
    ]);
  }
  if (!roleApproved('cco')) return uniqueReviewerNames(pending('cco'));

  return [];
};

export const getPendingReviewersForArtwork = (artwork = {}) => {
  const currentStage = Number(artwork.currentStage) || 0;
  if (currentStage < 1) return [];

  const pendingReviewerEntries = [1, 2, 3, 4]
    .filter(stageNum => stageNum <= currentStage)
    .flatMap(stageNum => getPendingReviewersForStage(artwork[`stage${stageNum}`], { ...artwork, currentStage: stageNum })
      .map(name => ({ name, stageNum })));

  return [...new Set(
    pendingReviewerEntries
      .filter(({ name, stageNum }) => name && (name !== 'Admin (upload required)' || stageNum === currentStage))
      .map(({ name }) => name)
  )];
};

export const getPendingReviewersForUser = (artwork = {}, user = {}) => {
  const currentStage = Number(artwork.currentStage) || 0;
  const userId = user._id || user.id || null;
  const userEmail = user.email || '';
  const userName = user.name || '';
  const pendingNames = [];

  [1, 2, 3, 4].filter(stageNum => stageNum <= currentStage).forEach(stageNum => {
    const stage = artwork[`stage${stageNum}`];
    if (!stage) return;

    if (Array.isArray(stage.submissions) && stage.submissions.length > 0) {
      stage.submissions.forEach(submission => {
        const pendingApproval = (submission.approvals || []).find(approval =>
          approval.status === 'pending' &&
          ((approval.assignedUserId && normalizeId(approval.assignedUserId) === normalizeId(userId)) ||
            (approval.assignedUserEmail && approval.assignedUserEmail.toLowerCase() === userEmail.toLowerCase()) ||
            (approval.assignedUserName && approval.assignedUserName.toLowerCase() === userName.toLowerCase()))
        );
        const supplierId = submission.supplierId?._id || submission.supplierId || null;
        if (pendingApproval && canUserApprove(artwork, stageNum, user.role, userId, userEmail, supplierId, userName)) {
          pendingNames.push(pendingApproval.assignedUserName || user.name || user.email || user.role);
        }
      });
      return;
    }

    const pendingApproval = (stage.approvals || []).find(approval =>
      approval.status === 'pending' &&
      ((approval.assignedUserId && normalizeId(approval.assignedUserId) === normalizeId(userId)) ||
        (approval.assignedUserEmail && approval.assignedUserEmail.toLowerCase() === userEmail.toLowerCase()) ||
        (approval.assignedUserName && approval.assignedUserName.toLowerCase() === userName.toLowerCase()))
    );
    if (pendingApproval && canUserApprove(artwork, stageNum, user.role, userId, userEmail, null, userName)) {
      pendingNames.push(pendingApproval.assignedUserName || user.name || user.email || user.role);
    }
  });

  return [...new Set(pendingNames)];
};

export const getStageSupplierSubmissionStatusForUser = (artwork = {}, stageNum = 3, user = {}) => {
  const stage = artwork[`stage${stageNum}`] || {};
  const userId = user._id || user.id || null;
  const userEmail = (user.email || '').toLowerCase();
  const userName = (user.name || '').toLowerCase();

  return (stage.submissions || []).filter(submission => {
    const pendingApproval = (submission.approvals || []).some(approval =>
      approval.status === 'pending' &&
      ((approval.assignedUserId && normalizeId(approval.assignedUserId) === normalizeId(userId)) ||
        (approval.assignedUserEmail && approval.assignedUserEmail.toLowerCase() === userEmail) ||
        (approval.assignedUserName && approval.assignedUserName.toLowerCase() === userName))
    );
    const supplierId = submission.supplierId?._id || submission.supplierId || null;
    return pendingApproval && canUserApprove(artwork, stageNum, user.role, userId, user.email || '', supplierId, user.name || '');
  }).map(submission => {
    const supplier = submission.supplierId || {};
    return {
      supplierId: String(supplier._id || supplier),
      supplierName: supplier.name || supplier.email || String(supplier._id || supplier),
      submitted: Boolean(submission.artworkUrl || submission.artworkFileName || submission.artworkData),
    };
  });
};

export const getStageStatus = (artwork, stageNum) => {
  const stage = artwork[`stage${stageNum}`];
  if (!stage) return 'not_started';
  return stage.status;
};

const normalizeId = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'object') {
    if (value._id) return String(value._id);
    return String(value);
  }
  return String(value);
};

export const canUserApprove = (artwork, stageNum, role, userId = null, userEmail = '', supplierId = null, userName = '') => {
  const stage = artwork[`stage${stageNum}`];
  if (!stage) return false;
  if (stageNum <= 2) {
    if (stage.status === 'completed') return false;
    if (stage.status === 'not_started') return false;
  }

  // Stage 3 & 4 are reviewed per-supplier — approvals live on each submission,
  // not on the stage itself. The parent stage status may be marked completed even
  // while individual supplier submissions still need review.
  if (stageNum === 3 || stageNum === 4) {
    const submissions = Array.isArray(stage.submissions) ? stage.submissions : [];
    if (submissions.length === 0) return false;

    const normalizedSupplierId = normalizeId(supplierId);
    const targetSubmissions = normalizedSupplierId
      ? submissions.filter(s => normalizeId((s.supplierId && s.supplierId._id) || s.supplierId) === normalizedSupplierId)
      : submissions;

    return targetSubmissions.some(sub => {
      return canApproveApprovalsArray(sub.approvals || [], role, userId, userEmail, userName);
    });
  }

  if (stage.status === 'completed') return false;
  return canApproveApprovalsArray(stage.approvals || [], role, userId, userEmail, userName);
};

// Shared logic: given a flat approvals array (either a stage's or a single
// submission's), can this user act on it right now for this role?
function canApproveApprovalsArray(approvals, role, userId, userEmail, userName = '') {
  const normalizedEmail = (userEmail || '').toLowerCase().trim();
  const normalizedName = (userName || '').toLowerCase().trim();
  const myApproval = approvals.find(a => {
    if (a.role !== role) return false;
    const approvalUserId = a.assignedUserId ? String(a.assignedUserId) : '';
    const approvalEmail = (a.assignedUserEmail || '').toLowerCase().trim();
    const approvalName = (a.assignedUserName || '').toLowerCase().trim();
    return (
      (!userId && !normalizedEmail && !normalizedName) ||
      (!approvalUserId && !approvalEmail && !approvalName) ||
      (approvalUserId && (approvalUserId === String(userId) || approvalUserId === String(userId?._id || ''))) ||
      (normalizedEmail && approvalEmail && approvalEmail === normalizedEmail) ||
      (normalizedName && approvalName && approvalName === normalizedName)
    );
  });
  if (!myApproval || myApproval.status === 'approved') return false;

  const roleApproved = (targetRole) => {
    const roleApprovals = approvals.filter(a => a.role === targetRole);
    return roleApprovals.length > 0 && roleApprovals.every(a => a.status === 'approved');
  };

  const roleReadyForNextStep = (targetRole) => {
    const TEAM_ROLES = ['rd', 'regulatory', 'qa'];
    if (!TEAM_ROLES.includes(targetRole)) {
      return roleApproved(targetRole);
    }
    const hasHead = approvals.some(a => a.role === targetRole && a.teamLevel === 'head');
    if (!hasHead) {
      return roleApproved(targetRole);
    }
    return approvals.some(a => a.role === targetRole && a.teamLevel === 'head' && a.status === 'approved');
  };

  if (role === 'regulatory') {
    return roleReadyForNextStep('rd');
  }
  if (['qa', 'coo', 'marketing'].includes(role)) {
    return roleReadyForNextStep('rd') && roleReadyForNextStep('regulatory');
  }
  if (role === 'cco') {
    return roleReadyForNextStep('qa');
  }
  if (['rd', 'regulatory', 'qa'].includes(role) && myApproval.teamLevel === 'head') {
    return true;
  }
  return true; // rd can always go first
}